/*
Copyright (C) 2023-2026 QuantumNous

This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.

This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License
along with this program. If not, see <https://www.gnu.org/licenses/>.

For commercial licensing, please contact support@quantumnous.com
*/
import { useEffect, useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { getRouteApi } from '@tanstack/react-router'
import {
  type SortingState,
  type VisibilityState,
  type ColumnDef,
  getCoreRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table'
import { formatTimestampToDate } from '@/lib/format'
import { useMediaQuery } from '@/hooks'
import { useTableUrlState } from '@/hooks/use-table-url-state'
import { DataTablePage } from '@/components/data-table'
import { DataTableColumnHeader } from '@/components/data-table'
import { StatusBadge } from '@/components/status-badge'
import { MaskedValueDisplay } from '@/components/masked-value-display'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { Button } from '@/components/ui/button'
import { Trash2 } from 'lucide-react'
import { toast } from 'sonner'
import { getRegisterCodes, searchRegisterCodes, deleteRegisterCode } from '../api'
import { REGISTER_CODE_STATUSES, SUCCESS_MESSAGES } from '../constants'
import type { RegisterCode } from '../types'

const route = getRouteApi('/_authenticated/register-codes/')

export function RegisterCodesTable() {
  const isMobile = useMediaQuery('(max-width: 640px)')
  const [sorting, setSorting] = useState<SortingState>([])
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({})
  const [isDeleting, setIsDeleting] = useState<number | null>(null)

  const {
    globalFilter,
    onGlobalFilterChange,
    columnFilters,
    onColumnFiltersChange,
    pagination,
    onPaginationChange,
    ensurePageInRange,
  } = useTableUrlState({
    search: route.useSearch(),
    navigate: route.useNavigate(),
    pagination: { defaultPage: 1, defaultPageSize: isMobile ? 10 : 20 },
    globalFilter: { enabled: true, key: 'filter' },
    columnFilters: [],
  })

  const { data, isLoading, isFetching, refetch } = useQuery({
    queryKey: [
      'register-codes',
      pagination.pageIndex + 1,
      pagination.pageSize,
      globalFilter,
    ],
    queryFn: async () => {
      const hasFilter = globalFilter?.trim()
      const params = {
        p: pagination.pageIndex + 1,
        page_size: pagination.pageSize,
      }

      const result = hasFilter
        ? await searchRegisterCodes({ ...params, keyword: globalFilter })
        : await getRegisterCodes(params)

      return {
        items: result.data?.items || [],
        total: result.data?.total || 0,
      }
    },
    placeholderData: (previousData) => previousData,
  })

  const registerCodes = data?.items || []

  const handleDelete = async (id: number) => {
    setIsDeleting(id)
    try {
      const result = await deleteRegisterCode(id)
      if (result.success) {
        toast.success(SUCCESS_MESSAGES.REGISTER_CODE_DELETED)
        refetch()
      }
    } finally {
      setIsDeleting(null)
    }
  }

  const columns: ColumnDef<RegisterCode>[] = useMemo(
    () => [
      {
        accessorKey: 'id',
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title='ID' />
        ),
        cell: ({ row }) => (
          <div className='w-[60px]'>{row.getValue('id')}</div>
        ),
      },
      {
        accessorKey: 'key',
        meta: { mobileTitle: true },
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title='注册码' />
        ),
        cell: function CodeCell({ row }) {
          const key = row.getValue('key') as string
          const maskedKey = `${key.slice(0, 3)}${'*'.repeat(5)}${key.slice(-4)}`
          return (
            <MaskedValueDisplay
              label='完整代码'
              fullValue={key}
              maskedValue={maskedKey}
              copyTooltip='复制代码'
              copyAriaLabel='复制注册码'
            />
          )
        },
        enableSorting: false,
      },
      {
        accessorKey: 'status',
        meta: { mobileBadge: true },
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title='状态' />
        ),
        cell: ({ row }) => {
          const statusValue = row.getValue('status') as number
          const statusConfig = REGISTER_CODE_STATUSES[statusValue]
          if (!statusConfig) return null
          return (
            <StatusBadge
              label={statusConfig.labelKey}
              variant={statusConfig.variant}
              showDot={statusConfig.showDot}
              copyable={false}
            />
          )
        },
      },
      {
        accessorKey: 'created_time',
        meta: { mobileHidden: true },
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title='创建时间' />
        ),
        cell: ({ row }) => (
          <div className='min-w-[140px] font-mono text-sm'>
            {formatTimestampToDate(row.getValue('created_time'))}
          </div>
        ),
      },
      {
        accessorKey: 'used_time',
        meta: { mobileHidden: true },
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title='使用时间' />
        ),
        cell: ({ row }) => {
          const usedTime = row.getValue('used_time') as number
          if (usedTime === 0) {
            return <span className='text-muted-foreground text-sm'>-</span>
          }
          return (
            <div className='min-w-[140px] font-mono text-sm'>
              {formatTimestampToDate(usedTime)}
            </div>
          )
        },
      },
      {
        accessorKey: 'used_user_id',
        meta: { mobileHidden: true },
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title='使用者' />
        ),
        cell: ({ row }) => {
          const userId = row.getValue('used_user_id') as number
          if (userId === 0) {
            return <span className='text-muted-foreground text-sm'>-</span>
          }
          return (
            <Tooltip>
              <TooltipTrigger
                render={
                  <StatusBadge
                    label={`用户 ${userId}`}
                    variant='neutral'
                    copyable={false}
                    className='cursor-help'
                  />
                }
              />
              <TooltipContent>
                <div className='text-xs'>
                  用户 ID: {userId}
                </div>
              </TooltipContent>
            </Tooltip>
          )
        },
      },
      {
        id: 'actions',
        cell: ({ row }) => (
          <div className='flex justify-end'>
            <Button
              variant='ghost'
              size='icon'
              className='h-8 w-8 text-destructive'
              onClick={() => handleDelete(row.original.id)}
              disabled={isDeleting === row.original.id}
            >
              <Trash2 className='h-4 w-4' />
            </Button>
          </div>
        ),
      },
    ],
    [handleDelete, isDeleting]
  )

  const table = useReactTable({
    data: registerCodes,
    columns,
    state: {
      sorting,
      columnVisibility,
      globalFilter,
      pagination,
      columnFilters,
    },
    onSortingChange: setSorting,
    onColumnVisibilityChange: setColumnVisibility,
    globalFilterFn: (row, _columnId, filterValue) => {
      const key = String(row.getValue('key')).toLowerCase()
      const id = String(row.getValue('id'))
      const searchValue = String(filterValue).toLowerCase()
      return key.includes(searchValue) || id.includes(searchValue)
    },
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    onPaginationChange,
    onGlobalFilterChange,
    onColumnFiltersChange,
    manualPagination: !globalFilter,
    pageCount: Math.ceil((data?.total || 0) / pagination.pageSize),
  })

  const pageCount = table.getPageCount()
  useEffect(() => {
    ensurePageInRange(pageCount)
  }, [pageCount, ensurePageInRange])

  return (
    <DataTablePage
      table={table}
      columns={columns}
      isLoading={isLoading}
      isFetching={isFetching}
      emptyTitle='暂无注册码'
      emptyDescription='暂无注册码数据，点击上方按钮生成注册码。'
      skeletonKeyPrefix='register-codes-skeleton'
      toolbarProps={{
        searchPlaceholder: '按注册码或 ID 过滤...',
      }}
    />
  )
}
